import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, Monitor, Check, Loader2, Save, SlidersHorizontal, ChevronDown } from "lucide-react";
import { http, rupiah, apiError } from "@/lib/api";
import AdminShell, { PageHead, useAdmin } from "@/components/AdminShell";
import { PRESETS, TOKEN_FIELDS, tokensToStyle } from "@/lib/themes";

const LETTERS = "ABCDEFGHIJKL".split("");

function MiniPreview({ tokens, kind }) {
  return (
    <div className="overflow-hidden rounded-xl font-body" style={tokensToStyle(tokens)}>
      {kind === "customer" ? (
        <div className="bg-background p-2.5">
          <div className="relative h-14 overflow-hidden rounded-lg bg-muted">
            <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 to-transparent p-1.5"><span className="font-heading text-[10px] font-extrabold text-white">Menu</span></div>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 rounded-lg bg-card p-1.5">
            <div className="h-7 w-7 rounded-md bg-muted" />
            <div className="flex-1"><div className="h-1.5 w-10 rounded bg-card-foreground/70" /><div className="mt-1 flex items-center justify-between"><span className="text-[9px] font-extrabold text-primary">Rp18k</span><span className="rounded-full bg-primary px-1.5 py-0.5 text-[7px] font-bold text-primary-foreground">Tambah</span></div></div>
          </div>
        </div>
      ) : (
        <div className="bg-background p-2.5">
          <div className="text-[7px] font-bold uppercase tracking-widest text-primary">Overview</div>
          <div className="font-heading text-[11px] font-extrabold text-foreground">Dashboard</div>
          <div className="mt-1.5 grid grid-cols-2 gap-1.5">
            <div className="rounded-md bg-card p-1.5"><div className="h-3 w-3 rounded bg-primary/30" /><div className="mt-1 h-1.5 w-8 rounded bg-card-foreground/60" /></div>
            <div className="rounded-md bg-card p-1.5"><div className="h-3 w-3 rounded bg-primary/30" /><div className="mt-1 h-1.5 w-6 rounded bg-card-foreground/60" /></div>
          </div>
        </div>
      )}
    </div>
  );
}

function ThemesInner() {
  const admin = useAdmin();
  const [s, setS] = useState(null);
  const [tab, setTab] = useState("customer_theme");
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { http.get("/admin/settings").then(({ data }) => setS({ ...data, admin_theme: admin.user?.admin_theme || data.admin_theme })).catch((e) => toast.error(apiError(e))); }, []);
  if (!s) return <div className="flex justify-center pt-20"><Loader2 className="wm-spin text-muted-foreground" /></div>;

  const theme = s[tab];
  const kind = tab === "customer_theme" ? "customer" : "admin";

  function pick(id) { const p = PRESETS[id]; setS((prev) => ({ ...prev, [tab]: { preset: id, type: p.type, tokens: { ...p.tokens } } })); }
  function setToken(key, val) { setS((prev) => ({ ...prev, [tab]: { ...prev[tab], preset: "custom", tokens: { ...prev[tab].tokens, [key]: val } } })); }

  async function save() {
    setBusy(true);
    try {
      await http.patch("/admin/settings", { customer_theme: s.customer_theme });
      await http.patch("/admin/my-theme", { admin_theme: s.admin_theme });
      admin.setStore((prev) => ({ ...prev, customer_theme: s.customer_theme }));
      admin.setUser && admin.setUser((u) => ({ ...u, admin_theme: s.admin_theme }));
      toast.success("Tema tersimpan & diterapkan");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <PageHead eyebrow="Tampilan" title="Tema & UI" desc="Tema Pelanggan berlaku global. Tema Admin bersifat pribadi per akun karyawan.">
        <button data-testid="save-theme-btn" onClick={save} disabled={busy} className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition-transform active:scale-95 disabled:opacity-50">
          {busy ? <Loader2 className="wm-spin" size={16} /> : <><Save size={16} /> Simpan & Terapkan</>}
        </button>
      </PageHead>

      {/* Target tabs */}
      <div className="mb-5 inline-flex rounded-2xl border border-border bg-card p-1">
        <button data-testid="tab-customer" onClick={() => setTab("customer_theme")} className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${tab === "customer_theme" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><Smartphone size={16} /> Tampilan Pelanggan</button>
        <button data-testid="tab-admin" onClick={() => setTab("admin_theme")} className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${tab === "admin_theme" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><Monitor size={16} /> Tampilan Admin</button>
      </div>

      {/* Gallery */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {Object.entries(PRESETS).map(([id, p], idx) => {
          const active = theme.preset === id;
          return (
            <button key={id} data-testid={`theme-card-${tab}-${id}`} onClick={() => pick(id)}
              className={`group relative overflow-hidden rounded-2xl border-2 p-2 text-left transition-all active:scale-95 ${active ? "border-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.25)]" : "border-border hover:border-primary/50"}`}>
              <span className="absolute right-2 top-2 z-10 grid h-6 w-6 place-items-center rounded-full bg-black/40 text-[11px] font-black text-white backdrop-blur">{active ? <Check size={13} /> : LETTERS[idx]}</span>
              <MiniPreview tokens={p.tokens} kind={kind} />
              <div className="mt-2 flex items-center justify-between px-1 pb-0.5">
                <span className="text-xs font-bold text-foreground">{p.name}</span>
                <span className="text-[9px] font-bold uppercase text-muted-foreground">{p.type}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Advanced custom */}
      <div className="mt-5 rounded-2xl border border-border bg-card">
        <button onClick={() => setAdvanced(!advanced)} className="flex w-full items-center justify-between p-4">
          <span className="flex items-center gap-2 text-sm font-bold text-card-foreground"><SlidersHorizontal size={16} /> Sesuaikan Warna Sendiri</span>
          <ChevronDown size={18} className={`text-muted-foreground transition-transform ${advanced ? "rotate-180" : ""}`} />
        </button>
        {advanced && (
          <div className="border-t border-border p-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              {TOKEN_FIELDS.map((f) => (
                <label key={f.key} className="flex items-center gap-2 rounded-xl bg-muted p-2">
                  <input type="color" data-testid={`color-${tab}-${f.key}`} value={theme.tokens[f.key] || "#000000"} onChange={(e) => setToken(f.key, e.target.value)} className="h-8 w-8 flex-none cursor-pointer rounded-lg border-0 bg-transparent p-0" />
                  <span className="truncate text-[11px] font-semibold text-card-foreground">{f.label}</span>
                </label>
              ))}
              <label className="flex items-center gap-2 rounded-xl bg-muted p-2">
                <span className="text-[11px] font-semibold text-card-foreground">Sudut</span>
                <select value={theme.tokens.radius || "1rem"} onChange={(e) => setToken("radius", e.target.value)} className="flex-1 rounded-lg bg-background px-1.5 py-1 text-[11px] outline-none">
                  <option value="0rem">Tajam</option><option value="0.5rem">Kecil</option><option value="1rem">Sedang</option><option value="1.5rem">Bulat</option>
                </select>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Big live preview */}
      <div className="mt-5 rounded-2xl border border-border bg-card p-4">
        <div className="mb-3 text-xs font-bold text-muted-foreground">Live Preview — {kind === "customer" ? "Halaman Pelanggan" : "Dashboard Admin"}</div>
        {kind === "customer" ? <BigCustomerPreview tokens={theme.tokens} storeName={s.store_name} /> : <BigAdminPreview tokens={theme.tokens} />}
      </div>
    </>
  );
}

function BigCustomerPreview({ tokens, storeName }) {
  return (
    <div data-testid="big-preview-customer" className="mx-auto max-w-xs overflow-hidden rounded-2xl border border-border font-body" style={tokensToStyle(tokens)}>
      <div className="bg-background p-4">
        <div className="relative h-24 overflow-hidden rounded-xl bg-muted"><div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 to-transparent p-2"><span className="font-heading text-sm font-extrabold text-white">{storeName || "Nama Kedai"}</span></div></div>
        <div className="mt-3 flex gap-2"><span className="rounded-full bg-primary px-3 py-1.5 text-[10px] font-bold text-primary-foreground">Makanan</span><span className="rounded-full bg-muted px-3 py-1.5 text-[10px] font-bold text-muted-foreground">Minuman</span></div>
        <div className="mt-3 flex gap-2 rounded-xl bg-card p-2"><div className="h-14 w-14 rounded-lg bg-muted" /><div className="flex-1"><div className="text-xs font-bold text-card-foreground">Mie Ayam Spesial</div><div className="mt-0.5 text-[10px] text-muted-foreground">Kuah gurih & pangsit</div><div className="mt-2 flex items-center justify-between"><span className="text-sm font-extrabold text-primary">{rupiah(18000)}</span><span className="rounded-full bg-primary px-2.5 py-1 text-[9px] font-bold text-primary-foreground">Tambah</span></div></div></div>
      </div>
    </div>
  );
}
function BigAdminPreview({ tokens }) {
  return (
    <div data-testid="big-preview-admin" className="overflow-hidden rounded-2xl border border-border font-body" style={tokensToStyle(tokens)}>
      <div className="bg-background p-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-primary">Overview</div>
        <div className="font-heading text-xl font-extrabold text-foreground">Dashboard</div>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {["Revenue", "Pesanan", "Pending", "Meja"].map((l, i) => (
            <div key={l} className="rounded-xl bg-card p-3"><div className="h-7 w-7 rounded-lg bg-primary/20" /><div className="mt-2 text-[9px] text-muted-foreground">{l}</div><div className="text-sm font-extrabold text-card-foreground">{i === 0 ? rupiah(240000) : 12 - i}</div></div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Themes() { return <AdminShell><ThemesInner /></AdminShell>; }
